import React, { useState } from 'react';
import { Bell, Zap, CheckCircle, XCircle, Loader2, Send, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminToolsTab = () => {
  const [pushResult, setPushResult] = useState(null);
  const [pushLoading, setPushLoading] = useState(false);
  const [nudgeResult, setNudgeResult] = useState(null);
  const [nudgeLoading, setNudgeLoading] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const handleTestPush = async () => {
    setPushLoading(true);
    setPushResult(null);
    try {
      const res = await fetch(`${API}/api/admin/test-push`, { method: 'POST', headers });
      const data = await res.json();
      setPushResult(data);
    } catch (err) {
      setPushResult({ success: false, error: err.message });
    } finally {
      setPushLoading(false);
    }
  };

  const handleTriggerNudges = async () => {
    setNudgeLoading(true);
    setNudgeResult(null);
    try {
      const res = await fetch(`${API}/api/admin/nudges/trigger`, { method: 'POST', headers });
      const data = await res.json();
      setNudgeResult(data);
    } catch (err) {
      setNudgeResult({ success: false, error: err.message });
    } finally {
      setNudgeLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-tools-tab">
      {/* Push Notification Debug Panel */}
      <div className="bg-white rounded-lg border p-5" data-testid="push-debug-panel">
        <div className="flex items-center gap-2 mb-4">
          <Bell className="h-5 w-5 text-indigo-600" />
          <h3 className="text-lg font-semibold text-gray-900">Push Notification Debug</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Send a test push notification to your browser. You must have push notifications enabled in your browser first.
        </p>
        <Button
          onClick={handleTestPush}
          disabled={pushLoading}
          className="bg-indigo-600 hover:bg-indigo-700 text-white"
          data-testid="test-push-btn"
        >
          {pushLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
          Send Test Push
        </Button>

        {pushResult && (
          <div className={`mt-4 p-4 rounded-lg border ${pushResult.success ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`} data-testid="push-result">
            <div className="flex items-center gap-2 mb-2">
              {pushResult.success ? (
                <><CheckCircle className="h-4 w-4 text-emerald-600" /><span className="font-medium text-emerald-800">Push sent successfully</span></>
              ) : (
                <><XCircle className="h-4 w-4 text-red-600" /><span className="font-medium text-red-800">Push failed</span></>
              )}
            </div>
            {pushResult.error && <p className="text-sm text-red-700">{pushResult.error}</p>}
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              <Badge variant="outline">VAPID: {pushResult.vapid_configured ? 'OK' : 'Missing'}</Badge>
              <Badge variant="outline">pywebpush: {pushResult.webpush_available ? 'OK' : 'Missing'}</Badge>
              <Badge variant="outline">FCM: {pushResult.fcm_available ? 'OK' : 'Off'}</Badge>
              <Badge variant="outline">Web Subs: {pushResult.web_subscriptions ?? '?'}</Badge>
              <Badge variant="outline">Native Tokens: {pushResult.native_tokens ?? '?'}</Badge>
              {pushResult.result && (
                <>
                  <Badge variant="outline" className="text-emerald-700">
                    Sent: {pushResult.result.total_sent ?? pushResult.result.sent ?? 0}
                  </Badge>
                  {(pushResult.result.total_failed > 0 || pushResult.result.failed > 0) && (
                    <Badge variant="outline" className="text-red-700">
                      Failed: {pushResult.result.total_failed ?? pushResult.result.failed ?? 0}
                    </Badge>
                  )}
                </>
              )}
            </div>
            {pushResult.result?.web?.errors?.length > 0 && (
              <div className="mt-2">
                <p className="text-xs font-medium text-red-700 mb-1">Web Push Errors:</p>
                {pushResult.result.web.errors.map((err, i) => (
                  <p key={i} className="text-xs text-red-600 break-all">{err}</p>
                ))}
              </div>
            )}
            {pushResult.result?.native?.errors?.length > 0 && (
              <div className="mt-2">
                <p className="text-xs font-medium text-amber-700 mb-1">Native Push Errors:</p>
                {pushResult.result.native.errors.map((err, i) => (
                  <p key={i} className="text-xs text-amber-600 break-all">{err}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Nudge Processor Panel */}
      <div className="bg-white rounded-lg border p-5" data-testid="nudge-processor-panel">
        <div className="flex items-center gap-2 mb-4">
          <Zap className="h-5 w-5 text-amber-600" />
          <h3 className="text-lg font-semibold text-gray-900">Nudge Processor</h3>
          <Badge variant="outline" className="text-xs text-gray-500">Auto-runs every 30 min</Badge>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Manually trigger the nudge processor to send pending push notification nudges (vendor no-reply, customer no-reply, idle proposals, approaching events).
        </p>
        <Button
          onClick={handleTriggerNudges}
          disabled={nudgeLoading}
          className="bg-amber-600 hover:bg-amber-700 text-white"
          data-testid="trigger-nudges-btn"
        >
          {nudgeLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Run Nudge Processor
        </Button>

        {nudgeResult && (
          <div className={`mt-4 p-4 rounded-lg border ${nudgeResult.success ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`} data-testid="nudge-result">
            {nudgeResult.success ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600" />
                  <span className="font-medium text-emerald-800">Nudge processing complete</span>
                </div>
                <div className="flex flex-wrap gap-3 text-sm">
                  <div className="flex items-center gap-1">
                    <span className="text-gray-600">Vendor nudges:</span>
                    <span className="font-semibold">{nudgeResult.results?.vendor_nudges ?? 0}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-600">Customer nudges:</span>
                    <span className="font-semibold">{nudgeResult.results?.customer_nudges ?? 0}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-600">Skipped:</span>
                    <span className="font-semibold">{nudgeResult.results?.skipped ?? 0}</span>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-2">Triggered at: {new Date(nudgeResult.triggered_at).toLocaleString()}</p>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <XCircle className="h-4 w-4 text-red-600" />
                <span className="font-medium text-red-800">Failed: {nudgeResult.error}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminToolsTab;
